import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { apiFetch } from '../../utils/api';
import {
  Mail,
  Send,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface EmailNotificationSectionProps {
  reportId: string;
  claimantName: string;
  claimantEmail?: string;
  healthStatus: 'GREEN' | 'YELLOW' | 'RED';
  missingDocsCount: number;
}

export default function EmailNotificationSection({
  reportId,
  claimantName,
  claimantEmail,
  healthStatus,
  missingDocsCount
}: EmailNotificationSectionProps) {
  const { getToken } = useAuth();
  const [email, setEmail] = useState(claimantEmail || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [portalToken, setPortalToken] = useState<string | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  // Fetch portal token on mount
  useEffect(() => {
    fetchPortalToken();
  }, [reportId]);

  const fetchPortalToken = async () => {
    try {
      setTokenLoading(true);
      const token = await getToken();
      const data = await apiFetch<{ portal_token: string | null; portal_url: string | null }>(
        `/api/v1/notifications/portal-token/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPortalToken(data.portal_token);
      setPortalUrl(data.portal_url);
    } catch (err) {
      console.error('Failed to fetch portal token:', err);
      // Don't show error — we'll show a disabled state
    } finally {
      setTokenLoading(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      setHistoryLoading(true);
      const token = await getToken();
      const data = await apiFetch<{ emails: any[] }>(
        `/api/v1/notifications/email-history/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailHistory(data.emails || []);
    } catch (err) {
      console.error('Failed to fetch email history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendDocumentRequest = async () => {
    if (!email) return;
    
    try {
      setSending(true);
      setError(null);
      const token = await getToken();
      
      await apiFetch(`/api/v1/notifications/send-document-request/${reportId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          recipient_email: email,
          recipient_name: claimantName
        })
      });
      
      setSent(true);
      setTimeout(() => setDialogOpen(false), 2000);
    } catch (err: any) {
      if (err.message?.includes('429')) {
        setError('Rate limited: An email was sent recently. Wait 24 hours or use force send.');
      } else {
        setError(err.message || 'Failed to send email');
      }
    } finally {
      setSending(false);
    }
  };

  const sendReminder = async (force: boolean = false) => {
    if (!email) return;
    
    try {
      setSending(true);
      setError(null);
      const token = await getToken();
      
      await apiFetch(`/api/v1/notifications/send-reminder/${reportId}?force=${force}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(email)
      });
      
      setSent(true);
      setTimeout(() => setDialogOpen(false), 2000);
    } catch (err: any) {
      if (err.message?.includes('429') && !force) {
        setError('Rate limited. Click "Force Send" to override.');
      } else {
        setError(err.message || 'Failed to send reminder');
      }
    } finally {
      setSending(false);
    }
  };

  const copyUploadLink = () => {
    if (portalUrl) {
      navigator.clipboard.writeText(portalUrl);
    }
  };

  const openPortal = () => {
    if (portalUrl) {
      window.open(portalUrl, '_blank');
    }
  };

  // Don't show if health is GREEN (no missing docs)
  if (healthStatus === 'GREEN' && missingDocsCount === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border p-6 mb-6" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
            <Mail className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Request Documents</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {missingDocsCount} document{missingDocsCount !== 1 ? 's' : ''} needed from client
            </p>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                fetchEmailHistory();
                setDialogOpen(true);
              }}
              style={{ background: 'var(--color-brand)' }}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Email Request
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Document Request</DialogTitle>
              <DialogDescription>
                Send an email to {claimantName} requesting the missing documents.
              </DialogDescription>
            </DialogHeader>
            
            {sent ? (
              <div className="py-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-verified)' }} />
                <p className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>Email Sent!</p>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Document request sent to {email}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      Recipient Email
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Upload Portal Link */}
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      Upload Portal Link
                      {tokenLoading && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>Loading...</span>
                      )}
                      {!tokenLoading && !portalUrl && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--color-needs-human-text)' }}>Token not available</span>
                      )}
                    </label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        value={portalUrl || 'Generating link...'}
                        readOnly
                        className="text-xs"
                        style={!portalUrl ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)' } : { background: 'var(--color-bg-base)' }}
                        disabled={!portalUrl}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={copyUploadLink}
                        title="Copy link"
                        disabled={!portalUrl}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={openPortal}
                        title="Open portal"
                        disabled={!portalUrl}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  {/* Email History */}
                  {emailHistory.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        Recent Emails ({emailHistory.length})
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {emailHistory.slice(0, 5).map((email, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between text-xs rounded p-2"
                            style={{ background: 'var(--color-bg-base)' }}
                          >
                            <div className="flex items-center gap-2">
                              {email.success ? (
                                <CheckCircle2 className="w-3 h-3" style={{ color: 'var(--color-verified)' }} />
                              ) : (
                                <AlertCircle className="w-3 h-3" style={{ color: 'var(--color-needs-human-text)' }} />
                              )}
                              <span style={{ color: 'var(--color-text-secondary)' }}>{email.email_type}</span>
                            </div>
                            <span style={{ color: 'var(--color-text-dim)' }}>
                              {new Date(email.sent_at).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendDocumentRequest}
                    disabled={sending || !email}
                    style={{ background: 'var(--color-brand)' }}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-4 pt-4 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
        <button
          onClick={copyUploadLink}
          disabled={!portalUrl}
          style={{ color: portalUrl ? 'var(--color-text-secondary)' : 'var(--color-text-dim)' }}
          className={`flex items-center gap-2 text-sm ${
            portalUrl
              ? 'hover:text-[var(--color-brand)]'
              : 'cursor-not-allowed'
          }`}
        >
          <Copy className="w-4 h-4" />
          Copy Upload Link
        </button>
        <a
          href={portalUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!portalUrl) {
              e.preventDefault();
            }
          }}
          style={{ color: portalUrl ? 'var(--color-text-secondary)' : 'var(--color-text-dim)' }}
          className={`flex items-center gap-2 text-sm ${
            portalUrl
              ? 'hover:text-[var(--color-brand)]'
              : 'cursor-not-allowed'
          }`}
        >
          <ExternalLink className="w-4 h-4" />
          Preview Portal
        </a>
      </div>
    </div>
  );
}
